import type { Association, AssociationEdgeDevice, AssociationGateway } from "@/stores/association"

/**
 * @description  
 */
class EdgeSupportService {
    /**
     * @description Service base URL (without trailing `/`)
     */
    public static base_url: string = import.meta.env.VITE_APP_EDGE_SUPPORT_SERVICE_URL
    public static axios: any

    public static init (axiosInstance: any) {
      EdgeSupportService.axios = axiosInstance 
    }


    // Associations
    public static list_my_associations() {
      return EdgeSupportService.axios.get(`${EdgeSupportService.base_url}/associations`)
    }

    public static create_association(association: Association){
      const payload: any = {
        name: association.name
      }
      // Add description if set
      if(association.description){
        payload.description = association.description
      }
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations`, payload)
    }

    public static update_association(association: Association){
      if(!association.id) return
      const payload: any = {
        name: association.name,
        description: association.description
      }
      return EdgeSupportService.axios.put(`${EdgeSupportService.base_url}/associations/${association.id}`, payload)
    }

    public static delete_association(id: number){
      return EdgeSupportService.axios.delete(`${EdgeSupportService.base_url}/associations/${id}`)
    }

    public static join_association(join_key: string){
      const payload: any = {
        join_key: join_key
      }
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/join`, payload)
    }

    public static leave_association(id: number){
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/${id}/leave`)
    }

    /** 
     * Kick out a team from an association. 
     * Caller must belong to the team who created the association 
     */
    public static remove_team_from_association(association_id: number, team_id: number){
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/${association_id}/kick?team_id=${team_id}`)
    }

    public static association_regenerate_join_key(association_id: number){
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/${association_id}/regenerate_join_key`)
    }

    public static list_gateways(association_id: number) {
      return EdgeSupportService.axios.get(`${EdgeSupportService.base_url}/associations/${association_id}/gateways`)
    }

    public static create_gateway(gateway: AssociationGateway) {
      const payload : any = {
        name: gateway.name,
        url: gateway.url,
        description: gateway.description
      }
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/${gateway.association_id}/gateways`, payload)
    }

    public static update_gateway(gateway: AssociationGateway) {
      if(!gateway.id) throw "No Gateway ID"
      const payload : any = {
        name: gateway.name,
        url: gateway.url,
        description: gateway.description
      }
      return EdgeSupportService.axios.put(`${EdgeSupportService.base_url}/associations/${gateway.association_id}/gateways/${gateway.id}`, payload)
    }

    public static delete_gateway(gateway: AssociationGateway){
      if(!gateway.id) throw "No Gateway ID"
      return EdgeSupportService.axios.delete(`${EdgeSupportService.base_url}/associations/${gateway.association_id}/gateways/${gateway.id}`)
    }

    public static get_gateway_apikeys(gateway: AssociationGateway) {
      if(!gateway.id) throw "No Gateway ID"
      return EdgeSupportService.axios.get(`${EdgeSupportService.base_url}/associations/${gateway.association_id}/gateways/${gateway.id}/api_key`)
    }
    
    public static generate_gateway_apikey(gateway: AssociationGateway, key_name: string|undefined) {
      const payload : any = {
        name: key_name
      }
      return EdgeSupportService.axios.put(`${EdgeSupportService.base_url}/associations/${gateway.association_id}/gateways/${gateway.id}/api_key`, payload)
    }

    public static delete_gateway_apikey(gateway: AssociationGateway, apikey_id: number) {
      // TODO should be DELETE
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/${gateway.association_id}/gateways/${gateway.id}/api_key/revoke/${apikey_id}`)
    }


    public static list_edge_devices_of_association(association_id: number) {
      return EdgeSupportService.axios.get(`${EdgeSupportService.base_url}/associations/${association_id}/edge_devices`)
    }

    public static list_edge_devices(): any {
      return EdgeSupportService.axios.get(`${EdgeSupportService.base_url}/associations/all/edge_devices`)
    }

    public static create_edge_device(edge_device: AssociationEdgeDevice) {
      // Construct payload for creating an ED: remove ID and association ID (sent in the URL)
      let payload: any = structuredClone(edge_device)
      delete payload.id
      delete payload.association_id
      return EdgeSupportService.axios.post(`${EdgeSupportService.base_url}/associations/${edge_device.association_id}/edge_devices`, edge_device)
    }

    public static update_edge_device(edge_device: AssociationEdgeDevice, remove_keys: boolean = false) {
      if(!edge_device.id) throw "No Edge Device ID"
      // Construct payload for creating an ED: remove association ID (sent in the URL) and the credentials if they are not being changed
      const payload : any = structuredClone(edge_device)
      delete payload.association_id
      if(remove_keys){
        delete payload.secret_access_key
        delete payload.access_key_id
      }
      return EdgeSupportService.axios.put(`${EdgeSupportService.base_url}/associations/${edge_device.association_id}/edge_devices/${edge_device.id}`, payload)
    }

    public static delete_edge_device(edge_device: AssociationEdgeDevice){
      if(!edge_device.id) throw "No edge_device ID"
      return EdgeSupportService.axios.delete(`${EdgeSupportService.base_url}/associations/${edge_device.association_id}/edge_devices/${edge_device.id}`)
    }
}

export default EdgeSupportService
